// src/component/datatable2/models/ColumnLayoutModel.ts
//
// Column widths of DataTable2. Framework-free: DataTable turns its <Column>
// children into ColumnLayoutItem descriptors and asks this class for the
// width of each one.

/** What the layout needs to know about one <Column>. */
export interface ColumnLayoutItem {
    key: string;
    width?: string | number;
    minWidth?: string | number;
    /** headerClassName, bodyClassName and className of the column. */
    classNames: ReadonlyArray<string | undefined>;
}

export class ColumnLayoutModel {
    /** Exact pixel width reserved for the actions column; never changes. */
    static readonly ACTIONS_COLUMN_WIDTH = 80;

    static readonly ACTIONS_COLUMN_CLASS = 'dt2-actions-column';

    /** Smallest width given to an auto-sized column without its own minWidth. */
    static readonly DEFAULT_MIN_WIDTH = 80;

    /** Widths are computed for at most this many pixels (the table design width). */
    static readonly DESIGN_TABLE_WIDTH = 1572;

    static parsePixels(value: string | number | undefined | null): number | null {
        if (value == null) {
            return null;
        }

        const parsed = parseFloat(String(value));

        return Number.isNaN(parsed) ? null : parsed;
    }

    static hasClass(value: string | undefined, className: string): boolean {
        return value?.split(/\s+/).includes(className) ?? false;
    }

    static isActionsColumn(column: Pick<ColumnLayoutItem, 'classNames'>): boolean {
        return column.classNames.some(value => ColumnLayoutModel.hasClass(value, ColumnLayoutModel.ACTIONS_COLUMN_CLASS));
    }

    /**
     * Width of every column, keyed by column key:
     *   • actions columns are locked to ACTIONS_COLUMN_WIDTH;
     *   • columns with an explicit width keep it;
     *   • the other columns share the remaining space equally (the last one
     *     also gets the leftover pixels), never below their minWidth.
     *
     * Note: the actions columns are subtracted from the available space twice
     * (once up front and once per column). That is how DataTable2 has always
     * sized its columns, so it is kept to leave the layout unchanged.
     */
    static computeWidths(columns: readonly ColumnLayoutItem[], containerWidth: number): Map<string, string> {
        const result = new Map<string, string>();
        const actionsWidth = ColumnLayoutModel.ACTIONS_COLUMN_WIDTH;

        let usedWidth = columns.filter(column => ColumnLayoutModel.isActionsColumn(column)).length * actionsWidth;
        const autoColumns: ColumnLayoutItem[] = [];

        for (const column of columns) {
            if (ColumnLayoutModel.isActionsColumn(column)) {
                result.set(column.key, `${actionsWidth}px`);
                usedWidth += actionsWidth;
                continue;
            }

            const explicitWidth = ColumnLayoutModel.parsePixels(column.width);

            if (explicitWidth != null) {
                usedWidth += explicitWidth;
                result.set(column.key, `${explicitWidth}px`);
            } else {
                autoColumns.push(column);
            }
        }

        if (autoColumns.length > 0) {
            const remaining = Math.max(containerWidth - usedWidth, 0);
            const share = Math.floor(remaining / autoColumns.length);
            const remainder = remaining - share * autoColumns.length;

            autoColumns.forEach((column, index) => {
                const width = index === autoColumns.length - 1 ? share + remainder : share;
                const minWidth = ColumnLayoutModel.parsePixels(column.minWidth) ?? ColumnLayoutModel.DEFAULT_MIN_WIDTH;

                result.set(column.key, `${Math.max(width, minWidth)}px`);
            });
        }

        return result;
    }

    /** Fixed style of the actions column (header, body and column). */
    static actionsColumnStyle() {
        const width = `${ColumnLayoutModel.ACTIONS_COLUMN_WIDTH}px`;

        return {
            width,
            minWidth: width,
            maxWidth: width,
            boxSizing: 'border-box' as const,
            overflow: 'hidden' as const,
        };
    }
}
